/**
 * ATL-1445 stage 3 — the backend's write-master refusal codes, and the ONE body shape the app acts on
 * (design `docs/technical/write-cutover-stage3-app-seam.md` §4 and §16; backend PR B1).
 *
 * WHAT B1 SENDS. A domain-W write that arrives WITHOUT the master header (`X-Atlas-Write-Mode: master`,
 * {@link WRITE_MODE_HEADER}) is today's Firestore-first MIRROR. For an organization whose
 * `writes_master` is `'supabase'` — the Supabase plane (`organizations.writes_master`); the database fence
 * decides under the `(1488, org)` lock, and B1 never reads the Firestore copy (#958 review 1) — Firestore
 * is no longer the source of truth, so B1 refuses that mirror with the body below. WHERE it runs is part of the contract (P8 review 1, S-5a): a CONTROLLER guard
 * ordered after `FirebaseAuthGuard` (and the role guard). Nest runs guards before every interceptor, and
 * the app's global `APP_INTERCEPTOR`s would otherwise answer first: `MigrationPauseInterceptor` answers
 * HTTP 200 `{ paused: true }` (which the v1 outbox takes as DELIVERED — a silent loss) and
 * `TruncatedIdInterceptor` answers its own 409. Only the auth guard's 401, the role guard's 403, and
 * Express body-parser rejections (413 over the 5 MB limit, `main.ts`; 400 for malformed JSON) — none of
 * which writes — may answer such a request before B1. Nothing is written. The response:
 *
 *   HTTP 409, AllExceptionsFilter envelope
 *   { statusCode: 409, timestamp, path,
 *     message: { code: 'WRITE_MASTER_SUPABASE', message: <text>, organizationFirestoreId: <org fs id> } }
 *
 * ({@link WriteMasterSupabaseRefusalBody} is the inner body: what B1 throws as
 * `new ConflictException(body)`.) The request-parity rule (design §7) makes the refused mirror
 * byte-identical to the master write the app would have sent, so the app RE-ENQUEUES that exact request
 * into outbox v2 as a master write (`origin: 'mirror-409-converted'`, P8) instead of losing it.
 *
 * WHY THE ORGANIZATION IS IN THE BODY. The app converts a refused request into ITS organization's master
 * queue. The backend resolves the organization from the caller's token, the app from its session; the
 * two agree for every write today, but a conversion files a write as the books of one organization, so
 * it is made only when the backend names the same one. A refusal without it, or naming another, is not
 * this contract and is left to today's handling.
 *
 * WHY UPPER CASE, AND NOT `write_master_supabase`. The backend already sends a lower-case
 * `write_master_supabase` (409) from `WritesMasterService.assertFirestoreIsWriteMaster`
 * (`writes-master.service.ts`): a Firestore-AS-TRUTH step refusing a Supabase-master organization. That
 * says "this operation may not run", not "this mirror must be re-sent as the master": it is NOT this
 * contract, and the parser below does not match it. ⚠️ It is called INSIDE W handlers — the product heal
 * from Firestore (`products.service.ts` `backfillProductFromFirestore`) and the supplier de-duplication
 * (`products.service.ts`, `supplies.service.ts`). On a MASTER-header write for a Supabase-master
 * organization those would answer that lower-case 409, which outbox v2's classifier
 * (`apps/atlas/global/Write/outboxClassify.ts`) reads as `master-changed`: the drainer pauses, re-reads
 * a flag that has not changed, resends, and loops. So it is a B1/P9 OBLIGATION that a master write never
 * reaches a Firestore heal: B1 must route master-header writes around every Firestore-as-truth step
 * (stage-3 §16 "The B1 contract"). `WRITE_MASTER_FIRESTORE` is likewise read as `master-changed`.
 *
 * A CONVERTED REQUEST CAN ARRIVE TWICE, so idempotent replay by document id is a B1 obligation (P8 review
 * 3, nit 3). The conversion appends to outbox v2 and only then removes the v1 original, and — unlike the
 * migration — it holds neither the v2 drain lock nor the migration barrier, so the v2 drainer may deliver
 * the copy first. A crash in that window re-converts the original under the same id and, the copy having
 * left v2 already, SENDS it again. Every W route must answer a master-header request whose document id it
 * has already applied as a replay (as the first time, or 2xx), writing nothing twice (stage-3 §16 "The B1
 * contract"; the return/manufacture/complex-sale gaps are B3's).
 *
 * `WRITE_MASTER_FIRESTORE` is reserved: B1 APPLIES a master-header write on a Firestore-master
 * organization (and reverse-mirrors it) rather than refusing it. If a backend ever sends it, outbox v2
 * pauses and re-reads the flag (P3's classifier); nothing here converts anything.
 *
 * EMITTED BY B1 (ATL-1445). `apps/atlas-backend/src/common/write-master/` sends it from the guard on
 * every domain-W route (and from its interceptor when the database refused an admitted mirror inside its
 * transaction because the organization flipped meanwhile) — for a mirror of a Supabase-master
 * organization only, so for every organization today nothing sends it. The contract is pinned at both
 * ends: B1's ordering spec drives a refused mirror through the real Nest stack and commits the response
 * to `apps/atlas-backend/src/common/write-master/__fixtures__/write-master-supabase-refusal.wire.json`;
 * `apps/atlas/global/Write/__tests__/mirrorRefusalContract.b1.test.js` runs this parser over those bytes
 * in all three error shapes, and holds B1 to being the only backend emitter.
 */

/** B1's refusal of a Firestore-first mirror for a Supabase-master organization. */
export const WRITE_MASTER_SUPABASE_CODE = 'WRITE_MASTER_SUPABASE' as const;

/** Reserved; see the module docblock. Never converted, only classified (outbox v2, P3). */
export const WRITE_MASTER_FIRESTORE_CODE = 'WRITE_MASTER_FIRESTORE' as const;

/** The exception body B1 throws (`new ConflictException(body)`); the envelope wraps it as `message`. */
export interface WriteMasterSupabaseRefusalBody {
  code: typeof WRITE_MASTER_SUPABASE_CODE;
  /** Human-readable; never parsed. */
  message: string;
  /** The organization's Firestore id, as the backend resolved it for this request. */
  organizationFirestoreId: string;
}

/** What the app needs from a matching refusal. */
export interface WriteMasterSupabaseRefusal {
  organizationFirestoreId: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** `{ code, organizationFirestoreId }` read off one candidate body, when it is this contract's. */
function refusalOfBody(body: unknown): WriteMasterSupabaseRefusal | null {
  if (!isRecord(body)) return null;
  if (body.code !== WRITE_MASTER_SUPABASE_CODE) return null;
  const org = body.organizationFirestoreId;
  if (typeof org !== 'string' || org.length === 0) return null;
  return { organizationFirestoreId: org };
}

/**
 * The B1 refusal carried by `error`, or `null` — for EVERY other error, including every response today's
 * backend can send. Reads the three shapes a write error arrives in:
 *  - `ApiRequestError` (the typed client's `request()`): `statusCode` 409 and the normalized body on
 *    `.error` (`normalizeApiErrorBody` keeps `code` and `organizationFirestoreId`);
 *  - a raw axios rejection (`replayRequest`, the outbox replays; `apiClient.js`): `response.status` 409
 *    and the envelope's inner body at `response.data.message`;
 *  - a raw axios rejection whose body is flat (`response.data` itself), for anything answering without
 *    the envelope.
 * Pure: it reads the error and nothing else, and never throws.
 */
export function parseWriteMasterSupabaseRefusal(error: unknown): WriteMasterSupabaseRefusal | null {
  try {
    if (!isRecord(error) && !(error instanceof Error)) return null;
    const e = error as unknown as Record<string, unknown>;
    if (typeof e.statusCode === 'number') {
      return e.statusCode === 409 ? refusalOfBody(e.error) : null;
    }
    const response = e.response;
    if (!isRecord(response) || response.status !== 409) return null;
    const data = response.data;
    if (!isRecord(data)) return null;
    return refusalOfBody(data.message) ?? refusalOfBody(data);
  } catch {
    // A getter that throws on a hostile error object is not a refusal.
    return null;
  }
}
